import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import type { TPersonParticipationShow } from '@schemas'

import { usePersonParticipation, usePersonParticipationEntries } from '@/api/hooks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { STAFF_ROLE_LABELS, STAFF_ROLE_ORDER } from '@/data/staff-roles'
import { formatDateRangeDateOnly } from '@/lib/date'
import { ENTRY_STATUS_LABELS } from '@/lib/entry-status'

type ParticipationTotals = { shows: number; showsEntered: number; entries: number }

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

// Offset pages can overlap when data changes between requests.
function uniqueBy<T>(items: T[], key: (item: T) => string): T[] {
  return [...new Map(items.map(item => [key(item), item])).values()]
}

function formatTotals(totals: ParticipationTotals) {
  return [
    plural(totals.shows, 'show', 'shows'),
    `${totals.showsEntered} with entries`,
    plural(totals.entries, 'entry', 'entries'),
  ].join(' · ')
}

function participationTotals(shows: TPersonParticipationShow[]): ParticipationTotals {
  return {
    shows: shows.length,
    showsEntered: shows.filter(row => row.entryCount > 0).length,
    entries: shows.reduce((sum, row) => sum + row.entryCount, 0),
  }
}

/** What one person would hold after a merge: a shared show counts once and its entries add up. */
export function combineParticipation(
  retained: TPersonParticipationShow[],
  duplicate: TPersonParticipationShow[]
): TPersonParticipationShow[] {
  const byShow = new Map<string, TPersonParticipationShow>()
  for (const row of [...retained, ...duplicate]) {
    const existing = byShow.get(row.show.id)
    byShow.set(
      row.show.id,
      existing
        ? {
            ...existing,
            roles: STAFF_ROLE_ORDER.filter(
              role => existing.roles.includes(role) || row.roles.includes(role)
            ),
            entrantRegistered: existing.entrantRegistered || row.entrantRegistered,
            entrantRecords: existing.entrantRecords + row.entrantRecords,
            entryCount: existing.entryCount + row.entryCount,
          }
        : row
    )
  }
  return [...byShow.values()].sort(
    (a, b) => b.show.startDate.localeCompare(a.show.startDate) || a.show.id.localeCompare(b.show.id)
  )
}

function ParticipationBadges({ row }: { row: TPersonParticipationShow }) {
  if (!row.roles.length && !row.entrantRegistered) return null
  return (
    <div className="flex flex-wrap gap-1.5">
      {row.roles.map(role => (
        <Badge key={role} variant="secondary">
          {STAFF_ROLE_LABELS[role].singular}
        </Badge>
      ))}
      {row.entrantRegistered ? <Badge variant="outline">Registered entrant</Badge> : null}
    </div>
  )
}

type PagedQuery = {
  isError: boolean
  isFetchNextPageError: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => unknown
  refetch: () => unknown
}

// A list that already has data can fail in two ways. A failed refresh leaves stale rows on
// screen and must say so; a failed next page only means the list is incomplete.
function RefreshFailed({ query, noun }: { query: PagedQuery; noun: 'participation' | 'entries' }) {
  if (!query.isError || query.isFetchNextPageError) return null
  const label = noun === 'entries' ? 'Entries' : 'Participation'
  return (
    <div role="alert" className="space-y-2 text-sm">
      <p>{label} could not be refreshed. What is shown may be out of date.</p>
      <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
        Retry {noun}
      </Button>
    </div>
  )
}

function MoreControls({ query, noun }: { query: PagedQuery; noun: 'shows' | 'entries' }) {
  if (query.isFetchNextPageError) {
    return (
      <div role="alert" className="flex items-center gap-2 text-sm">
        <p>More {noun} could not be loaded.</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void query.fetchNextPage()}
        >
          Retry more {noun}
        </Button>
      </div>
    )
  }
  if (query.isError || !query.hasNextPage) return null
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={query.isFetchingNextPage}
      onClick={() => void query.fetchNextPage()}
    >
      {query.isFetchingNextPage ? 'Loading more…' : `Load more ${noun}`}
    </Button>
  )
}

function ShowEntries({
  personId,
  showId,
  showSlug,
}: {
  personId: string
  showId: string
  showSlug: string
}) {
  const query = usePersonParticipationEntries(personId, showId)
  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading entries…
      </p>
    )
  }
  if (!query.data) {
    return (
      <div role="alert" className="space-y-2 text-sm">
        <p>Entries could not be loaded.</p>
        <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
          Retry entries
        </Button>
      </div>
    )
  }
  const entries = uniqueBy(
    query.data.pages.flatMap(page => page.entries),
    entry => entry.id
  )
  return (
    <div className="space-y-2">
      <RefreshFailed query={query} noun="entries" />
      <ul className="divide-y rounded-md border text-sm">
        {entries.map(entry => (
          <li key={entry.id} className="flex items-center justify-between gap-3 p-3">
            <div className="min-w-0">
              <p className="font-medium">
                <Link
                  to={`/${showSlug}/entries/${entry.id}`}
                  aria-label={`Open entry, Exhibit ID ${entry.entryCode}`}
                  className="font-mono underline underline-offset-2"
                >
                  {entry.entryCode}
                </Link>
                {entry.title ? <span> · {entry.title}</span> : null}
              </p>
              <p className="text-xs text-muted-foreground">
                {entry.class.code} · {entry.class.name}
              </p>
            </div>
            <Badge variant="outline">{ENTRY_STATUS_LABELS[entry.status]}</Badge>
          </li>
        ))}
      </ul>
      <MoreControls query={query} noun="entries" />
    </div>
  )
}

function ShowRow({ personId, row }: { personId: string; row: TPersonParticipationShow }) {
  // Entries load when the row is first opened and stay loaded while it is collapsed.
  const [opened, setOpened] = useState(false)
  return (
    <details className="group" onToggle={event => event.currentTarget.open && setOpened(true)}>
      <summary className="flex cursor-pointer list-none items-start gap-3 rounded-sm py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="mt-1 h-4 w-4 shrink-0 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <h4 className="font-medium">{row.show.name}</h4>
          <p className="text-xs text-muted-foreground">
            {formatDateRangeDateOnly(row.show.startDate, row.show.endDate)} ·{' '}
            {row.show.organization.name}
          </p>
          <ParticipationBadges row={row} />
        </div>
        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {plural(row.entryCount, 'entry', 'entries')}
        </span>
      </summary>
      <div className="space-y-3 pb-4 pl-7 text-sm">
        <Link to={`/${row.show.slug}`} className="underline underline-offset-2">
          Open {row.show.name}
        </Link>
        {row.entrantRecords > 1 ? (
          <p className="text-xs text-muted-foreground">
            Entries come from {row.entrantRecords} entrant records linked to this person.
          </p>
        ) : null}
        {row.entryCount === 0 ? (
          <p className="text-muted-foreground">
            {row.entrantRegistered
              ? 'Registered as an entrant with no entries.'
              : 'Staff only; not registered as an entrant.'}
          </p>
        ) : opened ? (
          <ShowEntries personId={personId} showId={row.show.id} showSlug={row.show.slug} />
        ) : null}
      </div>
    </details>
  )
}

/**
 * A person's participation in every show the viewer may see. Viewing, expanding and paging never
 * change the selected show; only following a link does.
 */
export function PersonParticipationHistory({ personId }: { personId: string }) {
  const query = usePersonParticipation(personId)
  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading participation…
      </p>
    )
  }
  if (!query.data) {
    return (
      <div role="alert" className="space-y-2 text-sm">
        <p>Participation could not be loaded.</p>
        <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
          Retry participation
        </Button>
      </div>
    )
  }
  const shows = uniqueBy(
    query.data.pages.flatMap(page => page.shows),
    row => row.show.id
  )
  return (
    <div className="space-y-2">
      <RefreshFailed query={query} noun="participation" />
      {shows.length ? (
        <>
          <p className="text-sm text-muted-foreground">
            {formatTotals(query.data.pages[0].totals)}
          </p>
          <div className="divide-y border-y">
            {shows.map(row => (
              <ShowRow key={row.show.id} personId={personId} row={row} />
            ))}
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          No participation recorded in shows you can access.
        </p>
      )}
      <MoreControls query={query} noun="shows" />
    </div>
  )
}

export type FullParticipation = {
  shows: TPersonParticipationShow[]
  status: 'idle' | 'loading' | 'failed' | 'complete'
  // Identifies what was reviewed, so the server can refuse a merge if it has changed since.
  reviewToken: string | undefined
  retry: () => void
}

/** Every page of a person's history, so a comparison is exact rather than "first page only". */
export function useFullParticipation(personId: string | null | undefined): FullParticipation {
  const query = usePersonParticipation(personId)
  const { data, hasNextPage, isFetching, isFetchingNextPage, isError, isFetchNextPageError } = query
  const { fetchNextPage, refetch } = query
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage])
  const shows = useMemo(
    () =>
      uniqueBy(
        (data?.pages ?? []).flatMap(page => page.shows),
        row => row.show.id
      ),
    [data]
  )
  // A background refresh can replace the rows and their review token, so cached rows are not
  // "complete" while one is running; otherwise a retry would resend the stale token.
  const status = !personId
    ? 'idle'
    : isError
      ? 'failed'
      : data && !hasNextPage && !isFetching
        ? 'complete'
        : 'loading'
  return {
    shows,
    status,
    reviewToken: data?.pages[0]?.reviewToken,
    retry: () => void (isFetchNextPageError ? fetchNextPage() : refetch()),
  }
}

/** Compact, link-free history for duplicate review; it also renders inside Check-In intake. */
export function ParticipationSummary({
  title,
  description,
  participation,
}: {
  title: string
  description?: string
  participation: Pick<FullParticipation, 'shows' | 'status'> & { retry?: () => void }
}) {
  const { shows, status, retry } = participation
  return (
    <section aria-label={title} className="space-y-2 rounded-md border p-3 text-sm">
      <div>
        <h4 className="font-medium">{title}</h4>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      </div>
      {status === 'failed' ? (
        <div role="alert" className="space-y-2">
          <p>Participation could not be loaded.</p>
          {retry ? (
            <Button type="button" variant="outline" size="sm" onClick={retry}>
              Retry participation
            </Button>
          ) : null}
        </div>
      ) : status !== 'complete' ? (
        <p role="status" className="text-muted-foreground">
          Loading participation…
        </p>
      ) : !shows.length ? (
        <p className="text-muted-foreground">No participation in shows you can access.</p>
      ) : (
        <>
          <p className="text-muted-foreground">{formatTotals(participationTotals(shows))}</p>
          <ul className="divide-y">
            {shows.map(row => (
              <li key={row.show.id} className="flex items-start justify-between gap-3 py-2">
                <div className="min-w-0 space-y-1">
                  <p className="font-medium">{row.show.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateRangeDateOnly(row.show.startDate, row.show.endDate)} ·{' '}
                    {row.show.organization.name}
                  </p>
                  <ParticipationBadges row={row} />
                </div>
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {plural(row.entryCount, 'entry', 'entries')}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}
